import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, RefreshCw, Hand, HelpCircle } from "lucide-react";

// Helper to check if all coins are same
const checkWinCondition = (coins) => {
  if (coins.length === 0) return false;
  const first = coins[0];
  return coins.every((c) => c === first);
};

const SpinningTableGame = () => {
  const navigate = useNavigate();
  
  // Game Configuration
  const [level, setLevel] = useState(1); // 1 = 4 cups, 2 = 8 cups
  const numCups = level === 1 ? 4 : 8;
  const maxHands = level === 1 ? 2 : 4;

  // Game State
  const [coins, setCoins] = useState([]); // true = Heads, false = Tails
  const [selectedCups, setSelectedCups] = useState([]); // indices of cups currently "touched"
  const [phase, setPhase] = useState("START"); // START, SELECT, ACTION, SPINNING, WIN
  const [message, setMessage] = useState("");
  const [rotation, setRotation] = useState(0); // visual rotation in degrees
  const [moves, setMoves] = useState(0);

  // Initialize Game
  const initGame = useCallback((lvl) => {
    const count = lvl === 1 ? 4 : 8;
    // thorough shuffle to ensure not already won
    let newCoins;
    do {
      newCoins = Array(count).fill(0).map(() => Math.random() < 0.5);
    } while (checkWinCondition(newCoins));

    setCoins(newCoins);
    setSelectedCups([]);
    setPhase("SELECT");
    setMoves(0);
    setRotation(0);
    setMessage(`Level ${lvl}: Select ${lvl === 1 ? "2" : "4"} cups to put your hands in.`);
  }, []);

  useEffect(() => {
    initGame(level);
  }, [level, initGame]);

  // Handlers
  const handleCupClick = (index) => {
    if (phase !== "SELECT") return;

    if (selectedCups.includes(index)) {
      setSelectedCups(selectedCups.filter((i) => i !== index));
    } else {
      if (selectedCups.length < maxHands) {
        setSelectedCups([...selectedCups, index]);
      }
    }
  };

  const handleInsertHands = () => {
    if (selectedCups.length !== maxHands) {
        setMessage(`You must select exactly ${maxHands} cups.`);
        return;
    }
    setPhase("ACTION");
    setMessage("Feel the coins! Click a coin to flip it. Click 'Remove Hands' when done.");
  };

  const handleCoinFlip = (index) => {
    if (phase !== "ACTION" || !selectedCups.includes(index)) {
        return;
    }

    const newCoins = [...coins];
    newCoins[index] = !newCoins[index]; // Flip
    setCoins(newCoins);
  };

  const handleRemoveHands = () => {
    setPhase("SPINNING");
    setMessage("Table is spinning... hold on...");
    setSelectedCups([]);

    // 1. Check win immediately? 
    // The riddle says "If all four coins have the same orientation AT ANY POINT"
    // So technically if I flip them to match, I win immediately before spin.
    // BUT, let's keep the mystery until the spin is done or just check now.
    // "After removing your hands, the table starts spinning... When it stops... you have solved the puzzle"
    // Usually you check *after* the move.
    
    const isWin = checkWinCondition(coins);
    
    // Animate spin
    const randomSpins = Math.floor(Math.random() * 5) + 5; // 5 to 10 full rotations
    // The shift must be unknown.
    // Since we re-render coins, visual alignment matters.
    // We will augment the visual rotation.
    const addedRotation = randomSpins * 360 + Math.floor(Math.random() * 360);
    setRotation((prev) => prev + addedRotation);

    setTimeout(() => {
        if (isWin) {
            setPhase("WIN");
            setMessage(`Puzzle Solved! The room unlocks. You escaped in ${moves + 1} moves.`);
        } else {
            // Apply logical shift (spin)
            // "You don't know how many rotations". 
            // We just shift the array by a random amount [0..N-1].
            const shift = Math.floor(Math.random() * numCups);
            const shiftedCoins = shiftArray(coins, shift);
            setCoins(shiftedCoins);
            
            setPhase("SELECT");
            setMoves((m) => m + 1);
            setMessage(`Moved! Table spun. Select ${maxHands} cups again.`);
        }
    }, 2000); // Wait for spin animation
  };

  const shiftArray = (arr, n) => {
      const L = arr.length;
      const shift = n % L;
      return [...arr.slice(L - shift), ...arr.slice(0, L - shift)];
  };

  // Render Helpers
  const getCupStyle = (index, total) => {
    const angle = (360 / total) * index;
    const radius = level === 1 ? 120 : 160; // larger radius for more cups
    // Positioning around center
    // transform: `rotate(${angle}deg) translate(${radius}px) rotate(-${angle}deg)`
    // This keeps the cup upright
    return {
        transform: `rotate(${angle}deg) translate(${radius}px) rotate(-${angle}deg)`,
    };
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <div className="mb-8 flex justify-between items-center">
            <button
                onClick={() => navigate("/games")}
                className="flex items-center text-blue-600 hover:text-blue-800 transition-colors"
                >
                <ArrowLeft className="h-5 w-5 mr-2" />
                Back to Games
            </button>
            <div className="flex space-x-4">
                <button 
                    onClick={() => setLevel(1)} 
                    className={`px-4 py-2 rounded-lg font-medium transition-colors ${level === 1 ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-100'}`}
                >
                    Level 1 (4 Cups)
                </button>
                <button 
                    onClick={() => setLevel(2)} 
                    className={`px-4 py-2 rounded-lg font-medium transition-colors ${level === 2 ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-100'}`}
                >
                    Level 2 (8 Cups)
                </button>
            </div>
        </div>

        <div className="bg-white rounded-xl shadow-xl overflow-hidden">
          <div className="p-8">
            <div className="flex flex-col items-center">
                <h1 className="text-3xl font-bold text-gray-900 mb-2">Spinning Table Puzzle</h1>
                <p className="text-gray-500 mb-8 max-w-2xl text-center">
                    You are in a dark room. The table has {numCups} coin cups. 
                    Can you get all coins to face the same way without seeing them?
                </p>

                {/* Game Area */}
                <div className="relative w-full max-w-[500px] aspect-square bg-gray-900 rounded-full border-8 border-gray-800 shadow-inner flex items-center justify-center mb-8 overflow-hidden">
                    {/* Interaction Layer (Rotates) */}
                    <div 
                        className="relative w-full h-full transition-transform duration-[2000ms] ease-in-out"
                        style={{ transform: `rotate(${rotation}deg)` }}
                    >
                        {coins.map((coin, i) => (
                            <div
                                key={i}
                                className={`absolute top-1/2 left-1/2 -ml-8 -mt-8 w-16 h-16 rounded-full flex items-center justify-center transition-all duration-300 shadow-lg z-10
                                ${
                                    selectedCups.includes(i) || phase === "WIN"
                                      ? "bg-amber-100 border-4 border-amber-300" 
                                      : "bg-gray-700 border-4 border-gray-600 cursor-pointer"
                                }`}
                                style={getCupStyle(i, numCups)}
                                onClick={() => handleCupClick(i)}
                            >
                                {/* Cup Content */}
                                { ((selectedCups.includes(i) && phase === "ACTION") || phase === "WIN") ? (
                                    <button 
                                        type="button"
                                        className={`w-10 h-10 rounded-full border-2 flex items-center justify-center text-xl font-bold focus:outline-none focus:ring-2 focus:ring-blue-500
                                        ${coin 
                                            ? "bg-yellow-400 border-yellow-600 text-yellow-900" 
                                            : "bg-gray-300 border-gray-400 text-gray-600"
                                        }`}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleCoinFlip(i);
                                        }}
                                    >
                                        {coin ? "H" : "T"}
                                    </button>
                                ) : (
                                    <div className="text-gray-500 font-bold text-2xl">?</div>
                                )}
                                
                                {/* Selection Indicator */}
                                {selectedCups.includes(i) && phase === "SELECT" && (
                                    <div className="absolute -top-2 -right-2 bg-blue-500 rounded-full p-1 shadow-md">
                                        <Hand className="w-4 h-4 text-white" />
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                    
                    {/* Central Status */}
                    <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
                         <div className="bg-black/50 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm font-medium">
                            {phase === "SPINNING" ? "Spinning..." : phase === "WIN" ? "UNLOCKED" : "Table Static"}
                         </div>
                    </div>
                </div>

                {/* Controls */}
                <div className="text-center space-y-4">
                    <p className={`text-lg font-medium transition-all ${phase === "WIN" ? "text-green-600 scale-110" : "text-gray-700"}`}>
                        {message}
                    </p>

                    <div className="flex items-center justify-center space-x-4 h-12">
                        {phase === "SELECT" && (
                             <button
                                onClick={handleInsertHands}
                                disabled={selectedCups.length !== maxHands}
                                className={`px-6 py-2 rounded-full font-bold shadow-md transition-all
                                ${selectedCups.length === maxHands 
                                    ? "bg-blue-600 text-white hover:bg-blue-700 hover:scale-105" 
                                    : "bg-gray-300 text-gray-500 cursor-not-allowed"}`}
                             >
                                Insert Hands ({selectedCups.length}/{maxHands})
                             </button>
                        )}

                        {phase === "ACTION" && (
                            <button
                                onClick={handleRemoveHands}
                                className="px-6 py-2 rounded-full bg-green-600 text-white font-bold shadow-md hover:bg-green-700 hover:scale-105 transition-all"
                            >
                                Remove Hands & Spin
                            </button>
                        )}
                        
                        {phase === "WIN" && (
                            <button
                                onClick={() => initGame(level)}
                                className="px-6 py-2 rounded-full bg-blue-600 text-white font-bold shadow-md hover:bg-blue-700 transition-all flex items-center"
                            >
                                <RefreshCw className="w-4 h-4 mr-2" /> Play Again
                            </button>
                        )}
                    </div>
                    
                     <div className="text-sm text-gray-400 mt-4">
                        Moves: {moves}
                    </div>
                </div>
            </div>
            
            {/* Rules Section */}
            <div className="bg-gray-50 p-6 border-t border-gray-100">
                <div className="flex items-start max-w-2xl mx-auto">
                    <HelpCircle className="w-6 h-6 text-blue-500 mr-3 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-gray-600 space-y-2">
                        <p className="font-semibold text-gray-800">How to play:</p>
                        <p>1. Select {maxHands} cups to examine.</p>
                        <p>2. "Insert Hands" to reveal the coins in those cups.</p>
                        <p>3. Click any revealed coin to flip it (Heads ↔ Tails).</p>
                        <p>4. "Remove Hands" to let the table spin.</p>
                        <p>5. The table spins randomly, so you lose track of which cup is which.</p>
                        <p>6. Goal: Make all coins face the same direction (All Heads or All Tails).</p>
                    </div>
                </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default SpinningTableGame;
